import type { AttachmentLink, ExecutionTask, PlanDocument, ProjectRecord, TaskComment } from "@/engine/types";
import { documentCode } from "@/features/docs/doc-model";
import { recordCode } from "@/store/record-ops";

export type FileKind = "image" | "file" | "link";
export type FileKindFilter = "all" | FileKind;
export type FileSourceGroup = "task" | "record" | "comment" | "document";
export type FileSourceFilter = "all" | FileSourceGroup;
export type FileOwnerKind = "task" | "record" | "document";

export const FILE_KIND_LABEL: Record<FileKind, string> = {
  image: "이미지",
  file: "파일",
  link: "링크",
};

export const FILE_SOURCE_LABEL: Record<FileSourceGroup, string> = {
  task: "작업",
  record: "기록",
  comment: "댓글",
  document: "문서",
};

export interface FileEntry {
  key: string;
  attachment: AttachmentLink;
  projectId: string;
  projectName: string;
  ownerKind: FileOwnerKind;
  ownerId: string;
  ownerTitle: string;
  ownerCode?: string;
  commentId?: string;
  addedAt: string;
  /** 빈 문자열이면 올린 사람을 모른다. */
  addedBy: string;
  kind: FileKind;
}

export interface FileListQuery {
  text: string;
  kind: FileKindFilter;
  source: FileSourceFilter;
  /** 빈 문자열이면 플랜을 좁히지 않는다. */
  projectId: string;
}

export interface FileIndexProject {
  id: string;
  name: string;
  executionTasks: Array<Pick<ExecutionTask, "id" | "title" | "attachments" | "comments">>;
  records: Array<Pick<ProjectRecord, "id" | "kind" | "seq" | "title" | "attachments" | "comments">>;
  documents?: Array<Pick<PlanDocument, "id" | "seq" | "title" | "attachments" | "comments" | "deletedAt" | "visibility" | "authorId">>;
}

export type FileIndexView = {
  /** 로컬 파일은 나만 문서를 숨기지 않는다. */
  host?: "local" | "cloud";
  actorId?: string;
};

export function fileKind(a: AttachmentLink): FileKind {
  if (
    a.kind === "image" ||
    !!a.mimeType?.startsWith("image/") ||
    a.url.startsWith("data:image/") ||
    /\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(a.url)
  ) {
    return "image";
  }
  if (a.kind === "link") return "link";
  if (a.kind === "file" || a.url.startsWith("data:") || a.size !== undefined) return "file";
  if (/^https?:/i.test(a.url)) return "link";
  return "file";
}

export function fileSourceGroup(entry: FileEntry): FileSourceGroup {
  if (entry.commentId) return "comment";
  return entry.ownerKind;
}

export function fileSourceCaption(entry: FileEntry): string {
  if (entry.commentId) {
    if (entry.ownerKind === "task") return "작업 댓글";
    if (entry.ownerKind === "document") return "문서 댓글";
    return "기록 댓글";
  }
  if (entry.ownerKind === "task") return "작업";
  if (entry.ownerKind === "document") return "문서";
  return "기록";
}

export function fileSourceTitle(entry: FileEntry): string {
  if (entry.ownerKind === "record") {
    const code = entry.ownerCode ? `${entry.ownerCode} ` : "";
    return `${code}${entry.ownerTitle || "제목 없는 기록"}`.trim();
  }
  if (entry.ownerKind === "document") {
    const code = entry.ownerCode ? `${entry.ownerCode} ` : "";
    return `${code}${entry.ownerTitle || "제목 없음"}`.trim();
  }
  return entry.ownerTitle || "제목 없는 작업";
}

export function fileOpenPath(entry: FileEntry): string {
  if (entry.ownerKind === "document") return `/projects/${entry.projectId}/docs/${entry.ownerId}`;
  const params = new URLSearchParams();
  params.set("owner", entry.ownerId);
  params.set("ownerKind", entry.ownerKind);
  if (entry.commentId) {
    params.set("tab", "comments");
    params.set("comment", entry.commentId);
  } else {
    params.set("tab", "attachments");
  }
  return `/projects/${entry.projectId}/files?${params.toString()}`;
}

export function collectFileEntries(projects: FileIndexProject[], view: FileIndexView = {}): FileEntry[] {
  const out: FileEntry[] = [];
  const host = view.host ?? "local";
  for (const project of projects) {
    const projectName = project.name || "이름 없는 플랜";
    for (const task of project.executionTasks) {
      const owner = { projectId: project.id, projectName, ownerKind: "task" as const, ownerId: task.id, ownerTitle: task.title };
      pushAll(out, owner, task.attachments);
      pushComments(out, owner, task.comments);
    }
    for (const record of project.records) {
      const owner = {
        projectId: project.id,
        projectName,
        ownerKind: "record" as const,
        ownerId: record.id,
        ownerTitle: record.title,
        ownerCode: recordCode(record),
      };
      pushAll(out, owner, record.attachments);
      pushComments(out, owner, record.comments);
    }
    for (const doc of project.documents ?? []) {
      if (doc.deletedAt) continue;
      if (host === "cloud" && doc.visibility === "private" && doc.authorId !== view.actorId) continue;
      const owner = {
        projectId: project.id,
        projectName,
        ownerKind: "document" as const,
        ownerId: doc.id,
        ownerTitle: doc.title,
        ownerCode: documentCode(doc.seq),
      };
      pushAll(out, owner, doc.attachments);
      pushComments(out, owner, doc.comments);
    }
  }
  out.sort((a, b) => (a.addedAt < b.addedAt ? 1 : a.addedAt > b.addedAt ? -1 : a.attachment.title.localeCompare(b.attachment.title, "ko")));
  return out;
}

/** 파일 화면의 규모. 탭과 출처를 바꿔도 같다. 없는 종류는 빼고, 워크스페이스는 플랜이 둘 이상일 때만 플랜 수를 붙인다. */
export function fileLibraryScale(entries: readonly FileEntry[], scope: "plan" | "workspace"): string {
  let image = 0;
  let file = 0;
  let link = 0;
  const plans = new Set<string>();
  for (const entry of entries) {
    if (entry.kind === "image") image += 1;
    else if (entry.kind === "link") link += 1;
    else file += 1;
    plans.add(entry.projectId);
  }
  const parts = [`${entries.length}개`];
  if (image > 0) parts.push(`이미지 ${image}`);
  if (file > 0) parts.push(`파일 ${file}`);
  if (link > 0) parts.push(`링크 ${link}`);
  if (scope === "workspace" && plans.size > 1) parts.push(`${plans.size}개 플랜`);
  return parts.join(" · ");
}

export type FileSortKey = "recent" | "name" | "size";

export function sortFileEntries(entries: readonly FileEntry[], key: FileSortKey): FileEntry[] {
  const next = [...entries];
  next.sort((a, b) => {
    if (key === "name") return a.attachment.title.localeCompare(b.attachment.title, "ko") || b.addedAt.localeCompare(a.addedAt);
    if (key === "size") return (b.attachment.size ?? -1) - (a.attachment.size ?? -1) || b.addedAt.localeCompare(a.addedAt);
    return a.addedAt < b.addedAt ? 1 : a.addedAt > b.addedAt ? -1 : a.attachment.title.localeCompare(b.attachment.title, "ko");
  });
  return next;
}

export function filterFileEntries(entries: FileEntry[], query: FileListQuery): FileEntry[] {
  const text = query.text.trim().toLowerCase();
  return entries.filter((entry) => {
    if (query.kind !== "all" && entry.kind !== query.kind) return false;
    if (query.source !== "all" && fileSourceGroup(entry) !== query.source) return false;
    if (query.projectId && entry.projectId !== query.projectId) return false;
    if (!text) return true;
    const hay = `${entry.attachment.title} ${entry.addedBy} ${entry.ownerTitle} ${entry.ownerCode ?? ""} ${entry.projectName}`.toLowerCase();
    return hay.includes(text);
  });
}

type Owner = Pick<FileEntry, "projectId" | "projectName" | "ownerKind" | "ownerId" | "ownerTitle" | "ownerCode">;

function pushAll(out: FileEntry[], owner: Owner, attachments: AttachmentLink[] | undefined, commentId?: string, commentAuthor?: string) {
  for (const attachment of attachments ?? []) pushOne(out, owner, attachment, commentId, commentAuthor);
}

function pushComments(out: FileEntry[], owner: Owner, comments: TaskComment[] | undefined) {
  for (const comment of comments ?? []) {
    if (comment.deleted) continue;
    pushAll(out, owner, comment.attachments, comment.id, comment.author);
  }
}

function pushOne(out: FileEntry[], owner: Owner, attachment: AttachmentLink | undefined, commentId?: string, commentAuthor?: string) {
  if (!attachment?.id || !attachment.url) return;
  out.push({
    ...owner,
    attachment,
    commentId,
    addedAt: attachment.addedAt || "",
    addedBy: (attachment.addedBy || commentAuthor || "").trim(),
    kind: fileKind(attachment),
    key: [owner.projectId, owner.ownerKind, owner.ownerId, commentId ?? "", attachment.id].join(":"),
  });
}
